import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Maximize2 } from 'lucide-react';
import { useRef, useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type Item = { id: string; name: string; icon: string; category: string; description: string; bestUse?: string };

// Category color palette - low saturation colors
const CATEGORY_COLORS: Record<string, { border: string; badge: string }> = {
  'Compare': { border: 'border-l-blue-400', badge: 'bg-blue-100 dark:bg-blue-900/30' },
  'Rank': { border: 'border-l-purple-400', badge: 'bg-purple-100 dark:bg-purple-900/30' },
  'Trend': { border: 'border-l-green-400', badge: 'bg-green-100 dark:bg-green-900/30' },
  'Distribution': { border: 'border-l-orange-400', badge: 'bg-orange-100 dark:bg-orange-900/30' },
  'Relationship': { border: 'border-l-pink-400', badge: 'bg-pink-100 dark:bg-pink-900/30' },
  'Part to whole': { border: 'border-l-cyan-400', badge: 'bg-cyan-100 dark:bg-cyan-900/30' },
  'Composition': { border: 'border-l-indigo-400', badge: 'bg-indigo-100 dark:bg-indigo-900/30' },
  'Flow': { border: 'border-l-teal-400', badge: 'bg-teal-100 dark:bg-teal-900/30' },
};

// Show all charts at once; no search or category filters

export default function ChartTypeGridDrawer({ open, onOpenChange, charts, onPick }: { open: boolean; onOpenChange: (o: boolean) => void; charts: Item[]; onPick: (id: string) => void; }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const clickLockedRef = useRef(false);

  function computePos(id: string): { left: number; top: number } | null {
    try {
      const el = cardRefs.current.get(id);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const width = 288; // ~w-72
      const height = 220; // approximate content height
      const margin = 8;
      let left = r.left + r.width / 2 - width / 2;
      left = Math.max(margin, Math.min(left, vw - width - margin));
      // Prefer above; if clipped, clamp to margin but keep as high as possible
      let top = r.top - height - margin;
      if (top < margin) top = margin;
      if (top + height + margin > vh) top = Math.max(margin, Math.min(r.bottom + margin, vh - height - margin));
      return { left, top };
    } catch { return null; }
  }

  function openPreview(id: string) {
    setOpenId(id);
    const p = computePos(id);
    if (p) setPos(p);
  }
  function closePreview() {
    setOpenId(null);
    setPos(null);
  }
  
  // Sort charts following the same sequence as ChartTypeShelves (lumichat)
  // Group by category, order categories, then sort alphabetically within each category
  // Flatten to single continuous array of charts (no headers in grid)
  const orderedCharts = useMemo(() => {
    const ORDER = ['Compare','Rank','Trend','Distribution','Relationship','Part to whole','Composition','Flow'];
    const byCat = new Map<string, Item[]>();
    
    // Group charts by category
    for (const c of charts) {
      const list = byCat.get(c.category) || [];
      list.push(c);
      byCat.set(c.category, list);
    }
    
    // Sort within each category alphabetically
    for (const [, arr] of byCat.entries()) {
      arr.sort((a, b) => a.name.localeCompare(b.name));
    }
    
    // Order categories according to ORDER, then append any remaining categories
    const orderedCats = ORDER.filter((c) => byCat.has(c)).concat(Array.from(byCat.keys()).filter((c) => !ORDER.includes(c)));
    
    // Flatten to single continuous array of charts
    const result: Item[] = [];
    for (const cat of orderedCats) {
      const catItems = byCat.get(cat) || [];
      result.push(...catItems);
    }
    
    return result;
  }, [charts]);

  // Get all unique categories for chips
  const categories = useMemo(() => {
    const cats = new Set<string>();
    orderedCharts.forEach(chart => {
      cats.add(chart.category);
    });
    return Array.from(cats);
  }, [orderedCharts]);

  // Filter charts by selected category
  const filteredCharts = useMemo(() => {
    if (!selectedCategory) return orderedCharts;
    return orderedCharts.filter(chart => chart.category === selectedCategory);
  }, [orderedCharts, selectedCategory]);

  // Scroll to first chart of category
  const scrollToCategory = (category: string) => {
    // Small delay to ensure DOM is updated after filter change
    setTimeout(() => {
      const firstChart = filteredCharts.find(c => c.category === category);
      if (firstChart) {
        const chartEl = cardRefs.current.get(firstChart.id);
        if (chartEl) {
          const scrollViewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
          if (scrollViewport) {
            const scrollContainer = scrollViewport;
            const containerRect = scrollContainer.getBoundingClientRect();
            const chartRect = chartEl.getBoundingClientRect();
            const scrollTop = scrollContainer.scrollTop + chartRect.top - containerRect.top - 20; // 20px offset
            scrollContainer.scrollTo({ top: scrollTop, behavior: 'smooth' });
          } else {
            // Fallback to standard scrollIntoView
            chartEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }
    }, 100);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="px-0 pt-0 pb-0 max-h-[90vh] max-w-5xl flex flex-col [&>button]:hidden" data-lumi="chart-drawer">
        <DialogTitle className="sr-only">Chart type picker</DialogTitle>
        
        {/* Sticky category chips */}
        <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b px-4 py-2">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
            <Button
              variant={selectedCategory === null ? 'secondary' : 'ghost'}
              size="sm"
              className={cn('text-xs h-7 whitespace-nowrap', selectedCategory === null && 'border-primary')}
              onClick={() => setSelectedCategory(null)}
            >
              All
            </Button>
            {categories.map((cat) => {
              return (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? 'secondary' : 'ghost'}
                  size="sm"
                  className={cn('text-xs h-7 whitespace-nowrap', selectedCategory === cat && 'border-primary')}
                  onClick={() => {
                    if (selectedCategory === cat) {
                      setSelectedCategory(null);
                    } else {
                      setSelectedCategory(cat);
                      // Scroll after a brief delay to allow filter to apply
                      setTimeout(() => scrollToCategory(cat), 50);
                    }
                  }}
                >
                  {cat}
                </Button>
              );
            })}
          </div>
        </div>

        <ScrollArea ref={scrollAreaRef} className="flex-1 max-h-[calc(90vh-60px)]">
          <div className="px-4 py-4">
            <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))' }}>
              {filteredCharts.map((c) => {
                const colors = CATEGORY_COLORS[c.category] || { border: 'border-l-gray-400', badge: 'bg-gray-100 dark:bg-gray-900/30' };
                const isCandle = (c.id || '').toLowerCase() === 'candlestick' || (c.name || '').toLowerCase() === 'candlestick';
                return (
                  <div
                    key={c.id}
                    className="relative group"
                    onMouseLeave={closePreview}
                    ref={(el) => { if (el) cardRefs.current.set(c.id, el); else cardRefs.current.delete(c.id); }}
                  >
                    {/* Category badge in corner */}
                    <div className={cn('absolute top-0 right-0 w-2 h-2 rounded-bl-md z-10', colors.badge)} />
                    
                    <button onClick={() => { if (clickLockedRef.current) return; clickLockedRef.current = true; onPick(c.id); onOpenChange(false); setTimeout(() => { clickLockedRef.current = false; }, 500); }} className="group aspect-square w-full rounded-md border bg-card p-1 flex flex-col hover:shadow-sm overflow-hidden">
                      <div className="flex-1 min-h-0 w-full h-full flex items-center justify-center">
                        <img
                          src={c.icon + (c.icon.includes('?') ? '&' : '?') + '_cb=' + ((window as any).__ICON_VERSION__ || Date.now())}
                          alt={c.name}
                          loading="lazy"
                          key={`${c.id}-${(window as any).__ICON_VERSION__ || '0'}`}
                          className={isCandle || c.id.toLowerCase() === 'polar area' || c.name.toLowerCase() === 'polar area' ? 'object-contain' : 'max-h-[88%] max-w-[88%] object-contain'}
                          style={(isCandle || c.id.toLowerCase() === 'polar area' || c.name.toLowerCase() === 'polar area') ? { height: '120%', width: 'auto', maxWidth: '100%' } : undefined}
                          onError={(e) => {
                            const el = e.currentTarget as HTMLImageElement;
                            const idx = Number(el.dataset.fallbackIndex || '0');
                            const base = '/chart_icons/';
                            const file = (c.icon || '').split('/').slice(-1)[0] || '';
                            const stem = file.replace(/\.svg$/i, '');
                            const slug = (s: string, sep: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, sep).replace(new RegExp(`${sep}+$`), '');
                            const candidates = [
                              `${base}${file}`,
                              `${base}${slug(c.name, '-')}.svg`,
                              `${base}${slug(c.name, '_')}.svg`,
                              `${base}${slug(c.name, '')}.svg`,
                              `${base}${slug(stem, '-')}.svg`,
                              `${base}${slug(stem, '_')}.svg`,
                              `${base}${slug(stem, '')}.svg`,
                            ];
                            const next = candidates.filter(u => u && u !== el.src);
                            if (idx < next.length) {
                              el.dataset.fallbackIndex = String(idx + 1);
                              el.src = next[idx];
                            }
                          }}
                          onMouseEnter={() => openPreview(c.id)}
                        />
                      </div>
                      <span className="mt-0.5 h-4 leading-4 text-[10px] line-clamp-1 text-center w-full">{c.name}</span>
                    </button>
                    <button
                      className="absolute top-1 right-1 hidden group-hover:flex items-center justify-center h-5 w-5 rounded bg-white/90 border text-foreground hover:bg-white z-20"
                      aria-label="Expand preview"
                      onClick={(e) => { e.stopPropagation(); openPreview(c.id); }}
                    >
                      <Maximize2 className="h-3 w-3" />
                    </button>

                    {openId === c.id && pos && (
                      <div
                        className="fixed z-50 w-72 p-3 rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-none"
                        style={{ left: pos.left, top: pos.top }}
                        onMouseLeave={closePreview}
                      >
                        <div className="flex items-center justify-center mb-2">
                          <img src={c.icon + (c.icon.includes('?') ? '&' : '?') + '_cb=' + ((window as any).__ICON_VERSION__ || Date.now())} alt={c.name} className="h-28 w-28 object-contain" key={`preview-${c.id}-${(window as any).__ICON_VERSION__ || '0'}`} />
                        </div>
                        <div className="text-sm font-medium mb-1">{c.name}</div>
                        <div className="text-xs text-muted-foreground">{c.description}</div>
                        {c.bestUse ? (
                          <div className="text-xs text-muted-foreground mt-1">Sample use: {c.bestUse}</div>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}


